import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { Card } from '../components/ui/card';
import { categories } from '../mock/mockData';
import { productsAPI, adsAPI } from '../services/api';
import { SlidersHorizontal, MapPin, Navigation, X } from 'lucide-react';

const FilterPanel = ({ filters, setFilters, toggleCategory, toggleCondition }) => (
  <>
    <div className="flex items-center mb-4">
      <SlidersHorizontal className="w-5 h-5 mr-2" />
      <h2 className="font-bold text-lg">Filtros</h2>
    </div>

    {/* Categories */}
    <div className="mb-6">
      <h3 className="font-semibold mb-3">Categorias</h3>
      <div className="space-y-2">
        {categories.slice(0, 6).map(cat => (
          <div key={cat.id} className="flex items-center space-x-2">
            <Checkbox
              id={`cat-${cat.id}`}
              checked={filters.categories.includes(cat.name)}
              onCheckedChange={() => toggleCategory(cat.name)}
            />
            <Label htmlFor={`cat-${cat.id}`} className="text-sm cursor-pointer">
              {cat.name}
            </Label>
          </div>
        ))}
      </div>
    </div>

    {/* Free Shipping */}
    <div className="mb-6">
      <div className="flex items-center space-x-2">
        <Checkbox
          id="free-shipping"
          checked={filters.freeShipping}
          onCheckedChange={(checked) =>
            setFilters(prev => ({ ...prev, freeShipping: checked }))
          }
        />
        <Label htmlFor="free-shipping" className="cursor-pointer">
          Envio gratis
        </Label>
      </div>
    </div>

    {/* Price Range */}
    <div className="mb-6">
      <h3 className="font-semibold mb-3">Precio</h3>
      <Slider
        min={0}
        max={3000000}
        step={50000}
        value={filters.priceRange}
        onValueChange={(value) =>
          setFilters(prev => ({ ...prev, priceRange: value }))
        }
        className="mb-2"
      />
      <div className="flex justify-between text-sm text-gray-600">
        <span>${filters.priceRange[0].toLocaleString()}</span>
        <span>${filters.priceRange[1].toLocaleString()}</span>
      </div>
    </div>

    {/* Condition */}
    <div className="mb-6">
      <h3 className="font-semibold mb-3">Condicion</h3>
      <div className="space-y-2">
        <div className="flex items-center space-x-2">
          <Checkbox
            id="nuevo"
            checked={filters.condition.includes('Nuevo')}
            onCheckedChange={() => toggleCondition('Nuevo')}
          />
          <Label htmlFor="nuevo" className="text-sm cursor-pointer">Nuevo</Label>
        </div>
        <div className="flex items-center space-x-2">
          <Checkbox
            id="usado"
            checked={filters.condition.includes('Usado')}
            onCheckedChange={() => toggleCondition('Usado')}
          />
          <Label htmlFor="usado" className="text-sm cursor-pointer">Usado</Label>
        </div>
      </div>
    </div>

    <Button
      variant="outline"
      className="w-full"
      onClick={() => setFilters({
        categories: [],
        freeShipping: false,
        priceRange: [0, 3000000],
        condition: []
      })}
    >
      Limpiar filtros
    </Button>
  </>
);

const ProductList = () => {
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const [products, setProducts] = useState([]);
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userLocation, setUserLocation] = useState(null);
  const [locationStatus, setLocationStatus] = useState('idle');
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const [filters, setFilters] = useState({
    categories: [],
    freeShipping: false,
    priceRange: [0, 3000000],
    condition: []
  });

  const [sortBy, setSortBy] = useState('relevance');

  const requestLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setLocationStatus('denied');
      return;
    }
    setLocationStatus('loading');
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocation({ lat: position.coords.latitude, lng: position.coords.longitude });
        setLocationStatus('granted');
      },
      () => { setLocationStatus('denied'); },
      { enableHighAccuracy: true, timeout: 5000, maximumAge: 300000 }
    );
  }, []);

  useEffect(() => { requestLocation(); }, [requestLocation]);

  const loggedQueryRef = useRef(null);

  const loadProducts = useCallback(async () => {
    try {
      setLoading(true);
      const shouldLog = !!query && loggedQueryRef.current !== query;
      if (shouldLog) loggedQueryRef.current = query;
      const [productsData, adsData] = await Promise.all([
        query ? productsAPI.search(query, userLocation, shouldLog) : productsAPI.getAllWithLocation({}, userLocation),
        adsAPI.getAdsForFeed({ limit: 10 })
      ]);
      setProducts(productsData);
      setAds(adsData);
      adsData.forEach(ad => {
        if (ad.campaign_id) adsAPI.recordImpression(ad.campaign_id).catch(() => {});
      });
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setLoading(false);
    }
  }, [query, userLocation]);

  useEffect(() => { loadProducts(); }, [loadProducts]);

  const handleAdClick = useCallback((campaignId) => {
    if (campaignId) adsAPI.recordClick(campaignId).catch(() => {});
  }, []);

  const filteredProducts = useMemo(() => {
    let filtered = [...products];
    if (filters.categories.length > 0) {
      filtered = filtered.filter(p => filters.categories.includes(p.category));
    }
    if (filters.freeShipping) {
      filtered = filtered.filter(p => p.free_shipping);
    }
    filtered = filtered.filter(p =>
      p.price >= filters.priceRange[0] && p.price <= filters.priceRange[1]
    );
    if (filters.condition.length > 0) {
      filtered = filtered.filter(p => filters.condition.includes(p.condition));
    }
    switch (sortBy) {
      case 'price_asc': filtered.sort((a, b) => a.price - b.price); break;
      case 'price_desc': filtered.sort((a, b) => b.price - a.price); break;
      case 'rating': filtered.sort((a, b) => b.rating - a.rating); break;
      case 'distance':
        filtered.sort((a, b) => {
          if (a.distance_km === null) return 1;
          if (b.distance_km === null) return -1;
          return a.distance_km - b.distance_km;
        });
        break;
      default:
        if (userLocation && filtered.some(p => p.distance_km != null)) {
          filtered.sort((a, b) => {
            if (a.distance_km === null) return 1;
            if (b.distance_km === null) return -1;
            return a.distance_km - b.distance_km;
          });
        }
        break;
    }
    return filtered;
  }, [products, filters, sortBy, userLocation]);

  const toggleCategory = (category) => {
    setFilters(prev => ({
      ...prev,
      categories: prev.categories.includes(category)
        ? prev.categories.filter(c => c !== category)
        : [...prev.categories, category]
    }));
  };

  const toggleCondition = (condition) => {
    setFilters(prev => ({
      ...prev,
      condition: prev.condition.includes(condition)
        ? prev.condition.filter(c => c !== condition)
        : [...prev.condition, condition]
    }));
  };

  const activeFilterCount = filters.categories.length
    + (filters.freeShipping ? 1 : 0)
    + filters.condition.length
    + (filters.priceRange[0] > 0 || filters.priceRange[1] < 3000000 ? 1 : 0);

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold mb-2">
            {query ? `Resultados para "${query}"` : 'Todos los productos'}
          </h1>
          <p className="text-gray-600">{filteredProducts.length} resultados</p>
        </div>

        {/* Mobile filter toggle */}
        <div className="md:hidden mb-4">
          <Button
            variant="outline"
            className="w-full flex items-center justify-center gap-2"
            onClick={() => setMobileFiltersOpen(true)}
            data-testid="mobile-filter-btn"
          >
            <SlidersHorizontal className="w-4 h-4" />
            Filtrar
            {activeFilterCount > 0 && (
              <span className="ml-1 bg-orange-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {activeFilterCount}
              </span>
            )}
          </Button>
        </div>

        {/* Mobile filter drawer (overlay) */}
        {mobileFiltersOpen && (
          <div className="fixed inset-0 z-50 md:hidden">
            <div className="absolute inset-0 bg-black/40" onClick={() => setMobileFiltersOpen(false)} />
            <div className="absolute inset-y-0 left-0 w-80 max-w-[85vw] bg-white shadow-xl overflow-y-auto" data-testid="mobile-filter-drawer">
              <div className="sticky top-0 bg-white z-10 flex items-center justify-between p-4 border-b">
                <h2 className="font-bold text-lg">Filtros</h2>
                <Button variant="ghost" size="icon" onClick={() => setMobileFiltersOpen(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
              <div className="p-4">
                <FilterPanel
                  filters={filters}
                  setFilters={(fn) => { setFilters(fn); }}
                  toggleCategory={toggleCategory}
                  toggleCondition={toggleCondition}
                />
                <Button
                  className="w-full mt-4 bg-orange-500 hover:bg-orange-600 text-white"
                  onClick={() => setMobileFiltersOpen(false)}
                >
                  Ver {filteredProducts.length} resultados
                </Button>
              </div>
            </div>
          </div>
        )}

        <div className="flex gap-6">
          {/* Desktop Filters Sidebar */}
          <aside className="w-64 flex-shrink-0 hidden md:block">
            <Card className="p-4 sticky top-24">
              <FilterPanel
                filters={filters}
                setFilters={setFilters}
                toggleCategory={toggleCategory}
                toggleCondition={toggleCondition}
              />
            </Card>
          </aside>

          {/* Products Grid */}
          <div className="flex-1 min-w-0">
            {/* Location Status Banner */}
            {locationStatus === 'denied' && (
              <Card className="p-4 mb-4 bg-yellow-50 border-yellow-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-yellow-800">
                    <MapPin className="w-5 h-5" />
                    <span className="text-sm">Activa tu ubicacion para ver tiempos de entrega estimados</span>
                  </div>
                  <Button size="sm" variant="outline" onClick={requestLocation}>
                    <Navigation className="w-4 h-4 mr-1" />
                    Activar
                  </Button>
                </div>
              </Card>
            )}

            {locationStatus === 'granted' && userLocation && (
              <Card className="p-3 mb-4 bg-green-50 border-green-200">
                <div className="flex items-center gap-2 text-green-700 text-sm">
                  <MapPin className="w-4 h-4" />
                  <span>Ubicacion activa - Mostrando tiempos de entrega estimados</span>
                </div>
              </Card>
            )}

            {/* Sort Options */}
            <Card className="p-4 mb-6">
              <div className="flex items-center flex-wrap gap-2">
                <span className="font-medium">Ordenar por:</span>
                <Button variant={sortBy === 'relevance' ? 'default' : 'ghost'} onClick={() => setSortBy('relevance')} size="sm">
                  Mas relevantes
                </Button>
                {userLocation && (
                  <Button variant={sortBy === 'distance' ? 'default' : 'ghost'} onClick={() => setSortBy('distance')} size="sm" className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" /> Mas cercanos
                  </Button>
                )}
                <Button variant={sortBy === 'price_asc' ? 'default' : 'ghost'} onClick={() => setSortBy('price_asc')} size="sm">
                  Menor precio
                </Button>
                <Button variant={sortBy === 'price_desc' ? 'default' : 'ghost'} onClick={() => setSortBy('price_desc')} size="sm">
                  Mayor precio
                </Button>
              </div>
            </Card>

            {/* Products */}
            {loading ? (
              <div className="text-center py-12">Cargando productos...</div>
            ) : filteredProducts.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {filteredProducts.map((product, index) => {
                  const items = [];
                  items.push(
                    <ProductCard
                      key={product._id}
                      product={product}
                      onAdClick={handleAdClick}
                    />
                  );
                  if ((index + 1) % 5 === 0 && ads.length > 0) {
                    const adIndex = Math.floor(index / 5) % ads.length;
                    const ad = ads[adIndex];
                    if (ad && ad._id !== product._id) {
                      items.push(
                        <ProductCard
                          key={`ad-${ad._id}-${index}`}
                          product={ad}
                          onAdClick={handleAdClick}
                        />
                      );
                    }
                  }
                  return items;
                })}
              </div>
            ) : (
              <Card className="p-12 text-center">
                <p className="text-xl text-gray-600">No se encontraron productos</p>
                <Button
                  className="mt-4"
                  onClick={() => setFilters({
                    categories: [],
                    freeShipping: false,
                    priceRange: [0, 3000000],
                    condition: []
                  })}
                >
                  Limpiar filtros
                </Button>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductList;
